import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import { cn } from "../utils";
import { capitalizeFirst } from "../format";
import { EmptyTableRow } from "./empty-table-row";
import { EmptyState, type EmptyStateProps } from "./empty-state";
import { CardList } from "./card-list";
import { DataTableInteractive } from "./data-table-interactive";

export interface DataTableColumn<R> {
  key: string;
  header: React.ReactNode;
  /** Optional width class, e.g. "w-32". */
  width?: string;
  align?: "left" | "right" | "center";
  /** Extra class on the <td>. */
  cellClassName?: string;
  /** Extra class on the <th>. */
  headerClassName?: string;
  render: (row: R, index: number) => React.ReactNode;
  /**
   * Ascending comparator. Presence makes this column sortable (and routes the
   * whole table to the interactive engine). Was `SortableTableColumn.sort`.
   */
  sort?: (a: R, b: R) => number;
  /** First click on this column sorts descending. Default ascending. */
  defaultDesc?: boolean;
  /** Set false to disable sorting even with a `sort` comparator. */
  sortable?: boolean;
  /** Omit the column entirely (e.g. when scoped out). */
  hidden?: boolean;
}

export interface DataTableProps<R> {
  rows: R[];
  columns: DataTableColumn<R>[];
  rowKey: (row: R, index: number) => string | number;
  /** Class on each <tr>. Receives the row for conditional styling. */
  rowClassName?: (row: R, index: number) => string | undefined;
  /** Optional DOM `id` on each <tr> (e.g. a `focus-<uid>` scroll/highlight anchor
   *  for Play Dive-in, §2.6). Return undefined to leave the row un-anchored. */
  rowId?: (row: R, index: number) => string | undefined;
  /** Optional click handler on each row. */
  onRowClick?: (row: R) => void;
  /** Optional `data-action-id` on each clickable row (verification action inventory). */
  rowActionId?: string;
  /**
   * Extra `data-*` attributes on each `<tr>`. The acceptance-hook API: a
   * browser test that reads rows by a domain selector
   * (`[data-cu107-capability-provider]`, say) needs the hook ON the row, and
   * `rowId`/`rowActionId` are already spoken for by focus anchors and the
   * action inventory. Returning a key with an `undefined` value omits that
   * attribute, so a conditional hook needs no spread gymnastics at the call
   * site.
   *
   * Keys must be `data-*` — the whole point is a test/inventory hook, not a
   * back door for arbitrary DOM props (`onClick`, `role`, `style`) that would
   * let a call site re-implement the row contract this block owns. Wired into
   * BOTH engines (static here, interactive in `data-table-interactive.tsx`),
   * so a table that later gains a `sort` comparator does not silently drop its
   * hooks.
   */
  rowAttributes?: (
    row: R,
    index: number,
  ) => Record<`data-${string}`, string | undefined>;
  /**
   * Empty-state node shown when rows.length === 0. A bare string/node renders
   * as a single muted line. For a proper blank state (icon + heading + guidance
   * + a "do this next" action), pass `emptyState` instead.
   */
  empty?: React.ReactNode;
  /**
   * Rich blank state shown when rows.length === 0. Renders the shared
   * `EmptyState` block spanning all columns, with the table's column count
   * wired in automatically. Tone rule: tell the user what to do next, not
   * just what's missing. Takes precedence over `empty`.
   */
  emptyState?: Omit<EmptyStateProps, "variant" | "colSpan">;
  /**
   * When true (default) the table is wrapped in a `<Card>` so it matches every
   * other surface in the shell. Set false when the table is already inside a
   * Card (e.g. a server-rendered page that has a CardHeader above the data).
   */
  wrapInCard?: boolean;
  /**
   * Optional integrated card header. When set (and the table is wrapped),
   * the Card grows a flush `<SectionHeader>` — the same header chrome the
   * "Recent activity" `ListCard` uses (px-4 py-3, bottom divider, a `Count`
   * pill, an optional right-aligned action). The table sits flush beneath it
   * and its flat 16px cell inset (`px-4`, table.tsx) lines the first column
   * up with the title — no double gutter, no inset header strip.
   *
   * Prefer this over hand-rolling `<Card><CardHeader><CardTitle>…</CardTitle>
   * </CardHeader><CardContent><DataTable wrapInCard={false}/></CardContent>
   * </Card>`, which insets the table from the card edge and drifts the header.
   */
  title?: React.ReactNode;
  /** Muted line below the title in the integrated header. */
  subtitle?: React.ReactNode;
  /** Count pill shown next to the title (matches the ListCard tally). */
  count?: number;
  /** Right-aligned slot in the integrated header (a link/button/filter). */
  headerAction?: React.ReactNode;
  /** Trailer row rendered flush below the table (e.g. a totals/"show all" line). */
  footer?: React.ReactNode;
  /**
   * Filter/search band rendered flush ABOVE the table, inside the table Card
   * (same recipe as DatabaseGrid). Prefer this over a toolbar floating above
   * the card so search, rows, and the pager share one edge.
   */
  toolbar?: React.ReactNode;
  /**
   * Optional caption band rendered flush ABOVE the column-header row, inside the
   * table's own Card. Pass a `<StatStrip variant="table">` to make a stat strip
   * the table's caption header (one Card, sealed by a hairline) instead of a
   * separate floating block above the table. Only rendered when wrapped in a Card.
   */
  summary?: React.ReactNode;
  /**
   * When true, render <TableBody> only and skip <TableHeader>. Used by
   * <FeedList> and any list that doesn't need column labels (single-column
   * feeds, recent-activity rails) but still wants atom-level styling.
   */
  hideHeader?: boolean;
  /**
   * Render the column-header labels at full `text-foreground` contrast instead
   * of the default muted tier. Off by default (the app-wide muted-header
   * convention holds); opt in on dense, data-first surfaces — the Database
   * Explorer tables — where the quiet header reads as too dim against the rows.
   */
  strongHeader?: boolean;
  /** Extra class on the outer wrapper. */
  className?: string;
  /** Optional class on the inner <table>. */
  tableClassName?: string;

  // ── Interactive modes (client) ────────────────────────────────────────────
  // Setting any of these routes the table to the `"use client"` engine
  // (`DataTableInteractive`) — the former SortableTable + MultiSelectTable, now
  // one engine on the one column type. Reach for them only from client
  // components (sort/select need client state).

  /** Enable client-side sorting (also auto-on when any column has `sort`). */
  sortable?: boolean;
  /** Starting sort. Implies the interactive engine. */
  initialSort?: { key: string; dir: "asc" | "desc" };
  /** Paginate at this page size, with a prev/next strip. */
  pageSize?: number;
  /** Keep the pager visible on a single page (disabled prev/next). */
  alwaysShowPager?: boolean;
  /** Enable j/k row focus + Enter-to-open (requires `rowHref`). */
  keyboardNav?: boolean;
  /** Per-row navigation target; row click / Enter pushes it. */
  rowHref?: (row: R) => string;
  /** Add a leading checkbox column + bulk selection (former MultiSelectTable). */
  selectable?: boolean;
  /** Bulk action bar shown above the table when ≥1 row is selected. */
  renderBulkBar?: (selected: R[], clear: () => void) => React.ReactNode;
  /** Render an extra row at the bottom (e.g. totals). Receives the visible rows. */
  footerRow?: (visibleRows: R[]) => React.ReactNode;
}

export function DataTable<R>(props: DataTableProps<R>) {
  // Interactive modes (sort / paginate / keyboard-nav / select / per-row href /
  // a footer row, or any column carrying a `sort` comparator) need client
  // state, so they route to the `"use client"` engine. The static path below
  // stays a shared component — a server page can pass `render` functions to it.
  if (
    props.sortable ||
    props.initialSort != null ||
    props.pageSize != null ||
    props.keyboardNav ||
    props.rowHref != null ||
    props.selectable ||
    props.footerRow != null ||
    props.columns.some((c) => c.sort != null)
  ) {
    return <DataTableInteractive {...props} />;
  }

  const {
    rows,
    columns,
    rowKey,
    rowClassName,
    rowId,
    onRowClick,
    rowActionId,
    rowAttributes,
    empty = "No results.",
    emptyState,
    wrapInCard = true,
    hideHeader = false,
    strongHeader = false,
    title,
    subtitle,
    count,
    headerAction,
    footer,
    toolbar,
    summary,
    className,
    tableClassName,
  } = props;

  const body = (
    <Table className={tableClassName}>
      {hideHeader ? null : (
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  col.width,
                  col.align === "right" && "text-right",
                  col.align === "center" && "text-center",
                  col.headerClassName,
                )}
              >
                {strongHeader ? (
                  <span className="font-medium text-foreground">
                    {typeof col.header === "string"
                      ? capitalizeFirst(col.header)
                      : col.header}
                  </span>
                ) : typeof col.header === "string" ? (
                  capitalizeFirst(col.header)
                ) : (
                  col.header
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      )}
      <TableBody>
        {rows.length === 0 ? (
          emptyState ? (
            <EmptyState
              variant="row"
              colSpan={columns.length}
              {...emptyState}
            />
          ) : (
            <EmptyTableRow colSpan={columns.length}>{empty}</EmptyTableRow>
          )
        ) : (
          rows.map((row, i) => (
            <TableRow
              key={rowKey(row, i)}
              id={rowId?.(row, i)}
              data-action-id={rowActionId}
              {...rowAttributes?.(row, i)}
              className={cn(
                // Interactive rows get the shared click affordance automatically
                // (PRD §3) — consumers never hand-roll cursor/hover. rowClassName
                // can still override (e.g. selected-row background).
                onRowClick && "cursor-pointer transition-colors hover:bg-secondary",
                rowClassName?.(row, i),
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  data-label={typeof col.header === "string" ? capitalizeFirst(col.header) : capitalizeFirst(col.key)}
                  className={cn(
                    col.align === "right" && "text-right tabular-nums",
                    col.align === "center" && "text-center",
                    col.cellClassName,
                  )}
                >
                  {col.render(row, i)}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  const toolbarBand =
    toolbar != null ? (
      <div className={cn("border-b border-border py-2.5", wrapInCard ? "px-4" : "mb-3")}>
        {toolbar}
      </div>
    ) : null;

  if (!wrapInCard) {
    return (
      <div className={className}>
        {toolbarBand}
        {body}
      </div>
    );
  }

  // The wrapped table IS a flush-card list: delegate the Card frame, the
  // integrated header (title/subtitle/count/action all map onto SectionTitle),
  // and the single footer bar to the one recipe (CardList, VERTICAL-RHYTHM-PRD
  // §T3). The `border border-border` keeps the table's slightly stronger edge.
  // No title → CardList omits the header (the former `showHeader` guard). The
  // summary band renders flush above the table, still inside the p-0 body.
  return (
    <CardList
      className={cn("border border-border", className)}
      title={title}
      subtitle={subtitle}
      count={count}
      action={headerAction}
      footer={footer}
    >
      {toolbarBand}
      {summary != null ? summary : null}
      {body}
    </CardList>
  );
}
